// Clerk application invitations arrive as `?__clerk_ticket=<JWT>` on whatever
// URL the Clerk instance is configured to redirect to. Our instance points at
// the sign-in page, but an invitee has no account yet — so the ticket has to
// be routed into the ticket-strategy SIGN-UP flow instead. Both auth pages
// therefore read the marker from the raw query string (TanStack's typed search
// drops unknown keys on routes that don't declare them).
//
// Clerk uses the same parameter name for a cross-domain session HANDSHAKE, so
// the value alone can't say which flow it belongs to. `st` ("subject type") in
// the ticket's JWT payload does: `invitation` for an application invitation,
// something else (e.g. `organization_invitation`, or absent) otherwise.
const INVITATION_SUBJECT_TYPE = 'invitation';

/**
 * The claims we read off a ticket. `sid` / `iid` are Clerk's own identifiers
 * for the invitation and instance — and they matter beyond routing: the ticket
 * carries NO email address, so on the sign-up landing they are the only thing
 * that can join this visit back to the invite that produced it (XT-706).
 */
export type TicketClaims = {
  st?: string;
  sid?: string;
  iid?: string;
};

const readString = (
  source: Record<string, unknown>,
  key: string
): string | undefined => {
  const value = source[key];
  return typeof value === 'string' ? value : undefined;
};

const decodeTicketClaims = (ticket: string): TicketClaims | undefined => {
  const payload = ticket.split('.').at(1);
  if (!payload) return;
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null) return;
    const source = parsed as Record<string, unknown>;
    return {
      st: readString(source, 'st'),
      sid: readString(source, 'sid'),
      iid: readString(source, 'iid'),
    };
  } catch {
    // Opaque or malformed ticket — not something we can route on.
    return;
  }
};

// Returns the raw ticket only when it is an application-invitation ticket.
export const readInvitationTicket = (search: string): string | undefined => {
  const ticket = new URLSearchParams(search).get('__clerk_ticket');
  if (!ticket) return;
  return decodeTicketClaims(ticket)?.st === INVITATION_SUBJECT_TYPE
    ? ticket
    : undefined;
};

/**
 * The invitation's own ids, for correlating this visit with the invite that
 * created it. Empty object for a malformed ticket — the caller still traces the
 * step, it just carries less.
 */
export const readTicketClaims = (ticket: string | undefined): TicketClaims =>
  ticket ? (decodeTicketClaims(ticket) ?? {}) : {};

// Browser-side convenience wrapper used by the auth forms.
export const currentInvitationTicket = (): string | undefined =>
  typeof window === 'undefined'
    ? undefined
    : readInvitationTicket(window.location.search);
